import { Injectable, OnDestroy } from '@angular/core';
import { ContentActionGroupInfo, DocumentAction } from '@office-start/document-types';
import { KnownAreas as SourceUXComponents } from '@office-start/instrumentation-types';

import { InstrumentationParams, KnownReferralPlatform } from '../../instrumentation/instrumentation.service';
import { MruFileItem } from '../../mru/mru-file-item.model';
import { OfficeStartBootstrapperService } from '../../office-start/office-start-bootstrapper.service';
import { SchedulePriority, SchedulerService } from '../../services/scheduler.service';
import { ContentActionsHelper } from './content-actions-helper.model';

@Injectable({
    providedIn: 'root'
})
export class ContentActionsService implements ContentActionsHelper, OnDestroy {
    private contentActionsHelperPromise: Promise<ContentActionsHelper | undefined>;
    private contentActionsHelper: ContentActionsHelper | undefined;
    constructor(
        private readonly officeStartBootstrapperService: OfficeStartBootstrapperService,
        private readonly schedulerService: SchedulerService
    ) {
    }

    public createToDoErrorNotification(errorMessage?: string) {
        this.maybeGetContentActionsHelper().then(contentActionsHelper => {
            contentActionsHelper?.createToDoErrorNotification(errorMessage);
        });
    }

    public createToDoSuccessNotification(callToAction: () => void, callToActionLabel?: string) {
        this.maybeGetContentActionsHelper().then(contentActionsHelper => {
            contentActionsHelper?.createToDoSuccessNotification(callToAction, callToActionLabel);
        });
    }

    public createDocumentActions(
        item: MruFileItem,
        itemElementId: string,
        sourceUXComponent: SourceUXComponents,
        itemCategory: string
    ): Promise<ReadonlyArray<DocumentAction>> {
        return this.maybeGetContentActionsHelper().then(contentActionsHelper => {
            if (!contentActionsHelper) {
                return [];
            }

            return contentActionsHelper.createDocumentActions(item, itemElementId, sourceUXComponent, itemCategory);
        }).catch(_reason => []);
    }

    public destroy() {
        if (this.contentActionsHelper) {
            this.contentActionsHelper.destroy();
        }
    }

    public getContentActionsGroupInfo(): Promise<ContentActionGroupInfo | undefined> {
        return this.maybeGetContentActionsHelper().then(contentActionsHelper => contentActionsHelper?.getContentActionsGroupInfo());
    }

    public ngOnDestroy() {
        this.destroy();
    }

    public runUnregisteredAction = (
        actionId: string,
        action: () => void,
        instrumentationArea: string,
        params: InstrumentationParams,
        referral?: string,
        referralPlatform?: KnownReferralPlatform) => {
            this.maybeGetContentActionsHelper().then(contentActionsHelper => {
                contentActionsHelper?.runUnregisteredAction(
                    actionId,
                    action,
                    instrumentationArea,
                    params,
                    referral,
                    referralPlatform
                );
            });
    }

    private maybeGetContentActionsHelper(): Promise<ContentActionsHelper | undefined> {
        return new Promise(resolve => {
            if (this.contentActionsHelper) {
                resolve(this.contentActionsHelper);
            }

            if (!this.contentActionsHelperPromise) {
                this.contentActionsHelperPromise = new Promise(resolveContentActionsHelper => {
                    this.schedulerService.scheduleWork(() => {
                        import(/*webpackChunkName: "content-actions-helper" */ './content-actions-helper')
                        .then(module => {
                            module.getContentActionsHelper(this.officeStartBootstrapperService)
                                .then(helper => {
                                    this.contentActionsHelper = helper;
                                    resolveContentActionsHelper(this.contentActionsHelper);
                                });
                        }).catch(_err => {
                            this.contentActionsHelper = undefined;
                            resolveContentActionsHelper(this.contentActionsHelper);
                        });
                    }, SchedulePriority.VeryLow);
                });
            }

            this.contentActionsHelperPromise.then(helper => {
                this.contentActionsHelper = helper;
                if (!this.contentActionsHelper) {
                    this.contentActionsHelperPromise = undefined;
                }
                resolve(this.contentActionsHelper);
            });
        });
    }
}
